import { Alert, Button, Card, ConfigProvider, Spin, theme } from 'antd';
import { FC, useMemo } from 'react';
import { Toggles } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import { AppRoutes, BaseQueryParams } from '@/common/interfaces';

import { DomainConfigInfo, DomainConfigurationStatus, DomainConfigurationStatusTag, DomainDeployStatus, DomainSchemeTag, useGetConfigDomainsQuery } from '@/modules/Domains';

import { DomainConfigInfoView } from './DomainConfigInfoView';
import { DomainNoConfigView } from './DomainNoConfigView';

interface Props {
  domainId: string;
  domainDeploymentInfo: BaseQueryParams<typeof useGetConfigDomainsQuery>['data'];
  isDomainDeploymentInfoLoading: boolean;
  isDeployDomainLoading: boolean;
  isShutdownDomainLoading: boolean;
  isDetachConfigLoading: boolean;
  handleDeployDomain: () => void;
  handleDetachDeploymentConfig: () => void;
}

const isConfigExist = (config: DomainConfigInfo | null): config is DomainConfigInfo => {
  return !!config;
};

export const DomainConfigOverviewView: FC<Props> = ({
  domainId,
  domainDeploymentInfo,
  isDeployDomainLoading,
  isDetachConfigLoading,
  isShutdownDomainLoading,
  isDomainDeploymentInfoLoading,
  handleDeployDomain,
  handleDetachDeploymentConfig,
}) => {
  const { token } = theme.useToken();

  const {
    isDeployed,
    deployStatus,
    configInfo,
    configurationStatus,
    configurationErrorMessage,
    isConfigured
  } = domainDeploymentInfo;

  const disabledByDeployStatuses = useMemo(
    () =>
      deployStatus === DomainDeployStatus.Pending ||
      deployStatus === DomainDeployStatus.Shutdowning ||
      deployStatus === DomainDeployStatus.Deploying,
    [deployStatus]
  );

  const disabledByLoadings =
    isDeployDomainLoading ||
    isShutdownDomainLoading ||
    isDetachConfigLoading ||
    isDomainDeploymentInfoLoading;

  const spinColor = useMemo(() => {
    if (configurationStatus === DomainConfigurationStatus.Attaching) {
      return token.blue5;
    } else if (configurationStatus === DomainConfigurationStatus.Detaching) {
      return token.geekblue5;
    } else if (configurationStatus === DomainConfigurationStatus.Pending) {
      return token.gold5;
    } else {
      return token.blue5;
    }
  }, [configurationStatus, token]);

  if (!isConfigured && (
    configurationStatus === DomainConfigurationStatus.NotConfigured ||
     configurationStatus === DomainConfigurationStatus.Failed
  )) {
    return (
      <DomainNoConfigView
        disableButtons={ isDomainDeploymentInfoLoading }
        domainId={ domainId }
        errorMessage={ configurationErrorMessage }
      />
    );
  } else {
    return (
      <Card
        title="Розгортання"
        size="small"
        styles={{ body: { minHeight: 48 } }}
        extra={
          (
            isConfigured && isConfigExist(configInfo) &&
            (configurationStatus === DomainConfigurationStatus.Failed ||
            configurationStatus === DomainConfigurationStatus.Configured)
          )
            ? <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Link to={ AppRoutes.DomainsByIdConfigEdit.replace(':domainId', domainId) }>
                <Button
                  disabled={ disabledByDeployStatuses || disabledByLoadings || isDeployed }
                  icon={ <Toggles/> }
                  size="small"
                  type="dashed"
                />
              </Link>
              <DomainSchemeTag isDeployed={ isDeployed } isHttps={ configInfo.isHttps }/>
            </div>
            : <DomainConfigurationStatusTag configurationStatus={ configurationStatus } isConfigured={ isConfigured }/>
        }
      >
        <ConfigProvider theme={{ components: { Spin: { colorPrimary: spinColor } } }}>
          <Spin
            style={{ paddingBlock: 12 }}
            spinning={
              configurationStatus === DomainConfigurationStatus.Attaching ||
              configurationStatus === DomainConfigurationStatus.Detaching ||
              configurationStatus === DomainConfigurationStatus.Pending
            }
          >
            {
              isConfigured && isConfigExist(configInfo) && (
                <DomainConfigInfoView
                  configInfo={ configInfo }
                  disabledByLoadings={ disabledByLoadings }
                  disabledByDeployStatuses={ disabledByDeployStatuses }
                  isConfigured={ isConfigured }
                  isDeployed={ isDeployed }
                  isDetachConfigLoading={ isDetachConfigLoading }
                  handleDeployDomain={ handleDeployDomain }
                  handleDetachDeploymentConfig={ handleDetachDeploymentConfig }
                />
              )
            }
            {
              configurationStatus === DomainConfigurationStatus.Failed && configurationErrorMessage && (
                <Alert
                  message={ configurationErrorMessage }
                  type="error"
                  showIcon
                />
              )
            }
          </Spin>
        </ConfigProvider>
      </Card>
    );
  }
};
